"use client";

import { useState } from "react";
import AppImage from "@/components/ui/AppImage";
import { useLanguage } from "@/context/LanguageContext";

export default function GallerySection() {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const { t } = useLanguage();

  const galleryImages = [
    {
      id: "gallery_001",
      src: "https://images.unsplash.com/photo-1527181617732-cd814bc53892",
      alt: "Tourist riding red quad bike through golden desert creating large dust trail",
      categoryKey: "act_quad" as const,
    },
    {
      id: "gallery_002",
      src: "https://img.rocket.new/generatedImages/rocket_gen_img_12c6cea6e-1765386842649.png",
      alt: "Dune buggy racing down steep sand dune with driver in full safety gear",
      categoryKey: "act_buggy" as const,
    },
    {
      id: "gallery_003",
      src: "https://images.unsplash.com/photo-1722336821495-504896bef48a",
      alt: "Camel caravan with tourists crossing desert at sunset with golden sky",
      categoryKey: "act_camel" as const,
    },
    {
      id: "gallery_004",
      src: "https://images.unsplash.com/photo-1703435420286-656b60e8ad0e",
      alt: "Rider on white Arabian horse galloping along beach with ocean waves",
      categoryKey: "act_horse" as const,
    },
    {
      id: "gallery_005",
      src: "https://images.unsplash.com/photo-1546198775-a6f6efce1ee4",
      alt: "Panoramic view of vast Tunisian desert with sand dunes at golden hour",
      categoryKey: "gallery_landscape" as const,
    },
    {
      id: "gallery_006",
      src: "https://images.unsplash.com/photo-1724671916729-c1258e4525a5",
      alt: "Happy family group posing with camels after desert trek in traditional clothing",
      categoryKey: "gallery_experience" as const,
    },
  ];

  return (
    <section className="py-20 md:py-32 bg-card">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12 reveal will-animate">
          <span className="text-accent font-semibold text-sm uppercase tracking-wider">
            {t("gallery_badge")}
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-foreground mt-4 mb-6">
            {t("gallery_title")}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t("gallery_desc")}
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {galleryImages?.map((image, index) => (
            <div
              key={image?.id}
              className="reveal will-animate group relative aspect-square overflow-hidden rounded-2xl cursor-pointer"
              onClick={() => setSelectedImage(index)}
            >
              <AppImage
                src={image?.src}
                alt={image?.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                <span className="text-white font-semibold">{t(image.categoryKey)}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Lightbox */}
        {selectedImage !== null && (
          <div
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <div className="relative max-w-5xl w-full">
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute -top-12 right-0 text-white hover:text-white/70 transition-colors"
                aria-label="Close lightbox"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <AppImage
                src={galleryImages?.[selectedImage]?.src}
                alt={galleryImages?.[selectedImage]?.alt}
                className="w-full h-auto rounded-2xl"
              />
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-12 reveal will-animate">
          <p className="text-muted-foreground mb-4">{t("gallery_follow")}</p>
          <a
            href="https://www.instagram.com/djerbatreasures"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full font-semibold hover:opacity-90 transition-all"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
            </svg>
            <span>@djerbatreasures</span>
          </a>
        </div>
      </div>
    </section>
  );
}